import { useState, useEffect, useMemo } from "react";
import { Row } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useStateMachine } from "little-state-machine";
import PropTypes from "prop-types";
import { useQueryClient } from "@tanstack/react-query";

import Alert from "components/molecules/Alert/Alert";
import Column from "components/atoms/Column/Column";
import Card from "components/molecules/Card/Card";
import { useParams } from "react-router-dom";
import { useGetQuickRequestDetails } from "api/queries/quick-request/useGetQuickRequestDetails";
import { useGetQuoteRequestDetails } from "api/queries/quotes/useGetQuoteRequestDetails";
import { useGetQuickRequestShipmentDetails } from "api/queries/quick-request/useGetQuickRequestShipmentDetails";
import CardNew from "components/molecules/Card/CardNew";
import { quickRequestQueryKeys } from "api/queries/quick-request/quickRequestQueryKeys";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import usePermissions from "hooks/usePermissions";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import { isShipmentDetailsRevampEnabled } from "./shipmentDetailsRevamp";
import QuickOrganisationalDetails from "./components/QuickOrganisationalDetails";
import ShipmentDetailsSheet from "./v2/ShipmentDetailsSheet";
import { mapQuoteDetailsToSheet } from "./v2/mapQuoteDetailsToSheet";
import { mapQuickDetailsToSheet } from "./v2/mapQuickDetailsToSheet";
import QuickSchedule from "./components/QuickSchedule";
import QuoteOrganisationalDetails from "./components/QuoteOrganisationalDetails";
import QuoteSchedule from "./components/QuoteSchedule";
import QuoteService from "./components/QuoteService";
import QuotePackingList from "./components/QuotePackingList";
import QuickService from "./components/QuickService";
import QuickPackingList from "./components/QuickPackingList";
import QuoteAttachments from "./components/QuoteAttachments";
import "./ShipmentDetails.scss";
/**
 * The Shipment details page (/shipment/:shipmentId/detail)
 */
const ShipmentDetails = function (props) {
  const { state } = useStateMachine();
  const { isQuickRequest } = props;
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [showDelayedMessage, setShowDelayedMessage] = useState(false);
  const { permissions } = usePermissions();
  const { permissions: permissionData, user_type } = JSON.parse(permissions || "{}");
  const companyShipmentDetailsRevampEnabled = useMemo(() => {
    const settings = JSON.parse(permissions || "{}")?.company_shipper_settings;
    return settings?.shipment_details_revamp_enabled;
  }, [permissions]);
  const [isCustomBroker] = useCustomBrokerPermission(isQuickRequest);

  const { data: quickDetails, isInitialLoading: quickDetailsLoading } = useGetQuickRequestDetails(id, {
    enabled: !!id && isQuickRequest,
  });

  const showQuickRequestShipmentDetails =
    !isQuickRequest && !permissionData?.quoting?.view_quote_requests && permissionData?.quoting?.view_quick_requests;

  const { data: quoteDetails, isInitialLoading: quoteDetailsLoading } = useGetQuoteRequestDetails({
    shipmentId: id,
    options: {
      enabled: !!id && !isQuickRequest && !showQuickRequestShipmentDetails,
      refetchOnMount: true,
    },
  });

  const { data: quickShipmentDetails, isInitialLoading: quickShipmentDetailsLoading } = useGetQuickRequestShipmentDetails(
    id,
    {
      enabled: !!id && !isQuickRequest && showQuickRequestShipmentDetails,
      refetchOnMount: true,
    },
  );

  const data = useMemo(
    () => (isQuickRequest ? quickDetails : showQuickRequestShipmentDetails ? quickShipmentDetails : quoteDetails) || {},
    [isQuickRequest, quickDetails, quoteDetails, showQuickRequestShipmentDetails, quickShipmentDetails],
  );
  const loadingData = isQuickRequest
    ? quickDetailsLoading
    : showQuickRequestShipmentDetails
    ? quickShipmentDetailsLoading
    : quoteDetailsLoading;
  const isDetailsRevampEnabled = isShipmentDetailsRevampEnabled(
    data?.shipment_details_revamp_enabled,
    companyShipmentDetailsRevampEnabled,
  );

  const delayedTimeoutMs = 20000;
  const timeoutMs = 40000;

  state.shipmentId = id;

  // Retrieve quote request/shipment data from API
  useEffect(() => {
    let isMounted = true;
    setTimeout(() => {
      if (isMounted) setShowDelayedMessage(true);
    }, delayedTimeoutMs);
    setTimeout(() => {
      if (isMounted)
        queryClient.cancelQueries(
          isQuickRequest ? quickRequestQueryKeys.quickRequestDetails(id) : quoteRequestQueryKeys.quoteDetails(id),
        );
    }, timeoutMs);

    return () => {
      isMounted = false;
    };
  }, []);

  const renderQuotePath = () => {
    if (isDetailsRevampEnabled) {
      const viewModel = mapQuoteDetailsToSheet(data, {
        userType: user_type,
        isCustomBroker,
        shipmentId: id,
      });
      return <ShipmentDetailsSheet viewModel={viewModel} loadingData={loadingData} userType={user_type} />;
    }

    return (
      <Row className="shipment-details-container">
        <Column xs={12} md={6} className="column-content">
          <QuoteOrganisationalDetails data={data} loadingData={loadingData} showDelayedMessage={showDelayedMessage} />
          <QuoteSchedule data={data} loadingData={loadingData} showDelayedMessage={showDelayedMessage} />
          <QuoteAttachments data={data} />
          <CardNew
            className={`mb-15 ${data?.organization?.additional_comments ? "" : "d-none"}`}
            isLoading={loadingData}
            title={
              <FormattedMessage
                id="quote-request-detail.additionalInformation"
                defaultMessage="Additional information"
              />
            }
          >
            <CardNew.Body>
              <Card.Text>{data?.organization?.additional_comments}</Card.Text>
            </CardNew.Body>
          </CardNew>
        </Column>

        <Column xs={12} md={6} className="column-content">
          <QuoteService data={data} loadingData={loadingData} showDelayedMessage={showDelayedMessage} />
          <QuotePackingList
            data={data}
            loadingData={loadingData}
            showDelayedMessage={showDelayedMessage}
            goods_confirmation_type={data?.organization?.goods_confirmation_type}
            organization_data={data?.organization}
          />
        </Column>
      </Row>
    );
  };

  const renderQuickRequestPath = () => {
    if (isDetailsRevampEnabled) {
      const viewModel = mapQuickDetailsToSheet(data, {
        userType: user_type,
        isCustomBroker,
        shipmentId: id,
      });
      return (
        <ShipmentDetailsSheet
          viewModel={viewModel}
          loadingData={loadingData}
          userType={user_type}
          isQuickRequest
        />
      );
    }

    return (
      <Row className="shipment-details-container">
        <Column xs={12} md={6} className="column-content">
          <QuickOrganisationalDetails data={data} loadingData={loadingData} showDelayedMessage={showDelayedMessage} />
          <QuickSchedule data={data} loadingData={loadingData} showDelayedMessage={showDelayedMessage} />
          <QuoteAttachments data={data} isQuickRequest />
          <CardNew
            className={`mb-15 ${data?.organization?.additional_comments ? "" : "d-none"}`}
            isLoading={loadingData}
            title={
              <FormattedMessage
                id="quote-request-detail.additionalInformation"
                defaultMessage="Additional information"
              />
            }
          >
            <CardNew.Body>
              <Card.Text>{data?.organization?.additional_comments}</Card.Text>
            </CardNew.Body>
          </CardNew>
        </Column>

        <Column xs={12} md={6} className="column-content">
          <QuickService data={data} loadingData={loadingData} showDelayedMessage={showDelayedMessage} />
          <QuickPackingList data={data} loadingData={loadingData} showDelayedMessage={showDelayedMessage} />
        </Column>
      </Row>
    );
  };

  return (
    <div>
      {Object.keys(data).length || loadingData ? (
        <div>
          {isQuickRequest ? (
            renderQuickRequestPath()
          ) : (
            renderQuotePath()
          )}
        </div>
      ) : (
        <Alert className="mb-0" variant="danger">
          <FormattedMessage
            id="quote-request.couldNotRetrieveDetailsMsg"
            defaultMessage="We could not retrieve any details. Please try again. If you continue to have trouble please contact admin."
          />
        </Alert>
      )}
    </div>
  );
};

ShipmentDetails.propTypes = {
  isQuickRequest: PropTypes.bool,
};

ShipmentDetails.defaultProps = {
  isQuickRequest: false,
};

export default ShipmentDetails;
